import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { PopupListItem, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';

import themed from 'webapp/decorators/themed';
import eq from 'webapp/helpers/eq';
import joinClasses from 'webapp/helpers/join-classes';
import BillingLimitationWarningComponent from 'webapp/components/billing/limitation-warning';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';

import { Severity } from 'webapp/services/logger';

interface Args {
	options: PopupListItem<string>[];
	onAction: (actionName: string, id?: string) => void;
	extraClass?: string;
	skipMainAction?: boolean;
	isDisabled?: boolean;
	hideDropdownSearch?: boolean;
	theme?: string;
	showMainActionIcon?: boolean;
}

@themed()
export default class OptionsButtonComponent extends Component<Args> {
	@service('logger') declare loggerService: Service.Logger;
	@service('popup') declare popupService: Service.Popup;

	declare themeClass: string; // from @themed
	declare sizeClass: string; // from @themed

	private isPopupOpen = false;

	get themeClassWithFallback() {
		return this.themeClass || 'theme-blue-outlined';
	}

	get mainAction() {
		return this.args.options[0]!;
	}

	get mainActionIcon(): IconName | undefined {
		return this.args.showMainActionIcon ? this.mainAction.icon : undefined;
	}

	get hasDropdown() {
		return this.args.options.length > 1 || this.isMainActionHidden;
	}

	get isMainActionHidden() {
		return this.args.options.length > 0 && this.args.skipMainAction;
	}

	get popupClasses() {
		let classes = this.args.extraClass || '';
		if (!this.args.skipMainAction) {
			if (classes) {
				classes += ' ';
			}
			classes += 'hide-first-option';
		}
		return classes;
	}

	@action
	doAction(actionName?: string, id?: string) {
		if (this.args.isDisabled) {
			return;
		}

		if (!actionName) {
			return;
		}

		this.args.onAction(actionName, id);
	}

	@action
	triggerMainAction() {
		const mainAction = this.mainAction;
		if (mainAction.isDisabled || !mainAction.action) {
			return;
		}

		this.doAction(mainAction.action);
	}

	@action
	openOptionsPopup(e: PointerEvent) {
		if (this.args.isDisabled) {
			return;
		}

		if (this.isPopupOpen) {
			void this.popupService.close();
			return;
		}

		const attachTo = (e.target as HTMLElement).closest<HTMLElement>(
			'.optionsbutton__settings',
		)!;

		if (!attachTo) {
			this.loggerService.logError(
				'[app/components/options-button.ts] attachTo not found?',
				undefined,
				Severity.WARNING,
			);
			return;
		}

		const popup = this.popupService.createListPopup(
			attachTo,
			undefined,
			this.popupClasses,
			true,
			true,
		);

		if (popup) {
			popup.open({
				itemsList: this.args.options,
				hideDropdownSearch: this.args.hideDropdownSearch,
				doAction: (actionName: string, id: string) => {
					if (isAlive(this)) {
						this.doAction(actionName, id);
					}
				},
			});

			this.isPopupOpen = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.isPopupOpen = false;
				}
			});
		}
	}
	<template>
		<div
			class={{joinClasses
				(array
					"optionsbutton"
					this.themeClassWithFallback
					this.sizeClass
					(unless this.hasDropdown "optionsbutton--no-settings")
					(unless this.mainAction "optionsbutton--no-main-action")
					(if this.mainAction.isDisabled "optionsbutton--main-action-disabled")
					(if @isDisabled "optionsbutton--disabled")
					(if this.isMainActionHidden "optionsbutton--main-action-hidden")
					@extraClass
				)
			}}
		>
			{{#if @options}}
				{{#unless @skipMainAction}}
					<div
						role="button"
						class="optionsbutton__main"
						{{on "click" this.triggerMainAction}}
					>
						{{#if this.mainActionIcon}}
							<CommonUiIconComponent @name={{this.mainActionIcon}} @size="sm" />
						{{/if}}
						<span>
							{{this.mainAction.text}}
							{{#if this.mainAction.isExternal}}
								<CommonUiIconComponent @name="External" @size="sm" />
							{{/if}}
						</span>
						{{#if (eq this.mainAction.extra.type "featureLimitation")}}
							<BillingLimitationWarningComponent />
						{{else if (eq this.mainAction.extra.type "limitReached")}}
							<ToolTipComponent @text={{this.mainAction.extra.text}} />
						{{/if}}
					</div>
				{{/unless}}
				<div
					role="button"
					class="optionsbutton__settings"
					{{on "click" this.openOptionsPopup}}
				>
					<CommonUiIconComponent @name="ChevronDown" @size="md" />
				</div>
			{{/if}}
		</div>
	</template>
}
